import { decorate, observable, action, computed, toJS, intercept } from "mobx";
import ApiClient from "utils/ApiClient";
import { sleep } from "../../utils/Utils";
import TableStore from "./views/table/TableStore";
import { createContext } from "react"
import Permissions from "./Permissions/Permissions";
import RemindersStore from "./Reminders/RemindersStore";
import ViewAggregationStore from "../ViewAggregation/ViewAggregationStore";
import { cloneDeep } from "lodash";
import { handleUpdatingData, handleSubsheetDataUpdationThroughForm } from "app/websocketManager/eventEmitter";

class ContainerStore {
    views = [];
    isLoading = true;
    containers = {};
    // viewStores = {};
    saveResetButtonsPosition = "snackbar"
    currentViewStore = null;
    isViewInEditMode = false;
    unSavedViewOfEditReport = null;
    initialViewOfEditReport = null;
    initialViewId = null;
    commentsPopoverData = {
        isCommentsPopoverOpen: false,
        commentsPopoverRowId: null,
        commentsPopoverAnchorEl: null
    }

    constructor(appStore, listId) {
        this.appStore = appStore;
        this.viewAggregationStore = new ViewAggregationStore(this.appStore);
        this.listId = listId
    }

    getCurrentViewStore() {
        if (!this.listConfig || !this.currentViewStore) {
            return null;
        }

        return this.currentViewStore;
    }

    setCurrentViewStore(view) {
        if (this.permissions?.isAdmin() && view.type === "table" && this.listId === window.userListId) {
            TableStore.addSpecialColumns(view.options.columns);
        }

        this.currentViewStore = new TableStore({
            listConfig: this.listConfig,
            view: view,
            listId: this.listId,
            viewId: view.id,
        }, this);
    }

    setViewAggregationStoreData(viewColumns) {
        this.viewAggregationStore.setViewAggregationData(viewColumns, this.listConfig, this.listId, this.view);
    }

    handleViewChange = (e, force = false, view = null) => {
        const viewId = e.target?.value ?? e;
        const oldViewId = this.view.id;

        this.appStore.currentViewStore = null;
        if (force) {
            this.currentViewStore?.showLoadingOverlay?.();
            this.currentViewStore = null;
        }
        if (view) {
            if (viewId != oldViewId) {
                this.currentViewStore?.showLoadingOverlay?.();
                this.currentViewStore = null;
            }
            if (!this.currentViewStore) {
                this.setCurrentViewStore(view);
            }
            this.view = view;
            return;
        }

        this.currentViewStore?.showLoadingOverlay?.();
        this.fetchView(viewId, true);
    };

    async setListColumns(listColumns) {
        this.listConfig.listData = listColumns;
    }

    async addDropdownOption (columnConfig) {
        const viewColumns = this.view.options.columns.map(col => {
            if (col.id === columnConfig.id) {
                Object.keys(columnConfig).forEach(
                    (key) => (col[key] = columnConfig[key])
                );
                return { ...col, ...columnConfig };
            }
            return col;
        });

        this.view.options.columns = viewColumns;
        this.listConfig.listData = this.listConfig.listData.map(col => {
            if (col.id === columnConfig.id) {
                return { ...col, ...columnConfig };
            }
            return col;
        });
        this.updateListColumns();
    }

    async addColumn(columnConfig, isScrollToAddColumn = true) {
        let isOldColumn = false;
        const viewColumns = this.view.options.columns.map(col => {
            if (col.id === columnConfig.id) {
                isOldColumn = true;
                col.header = this.view.is_main_view == 1 ? columnConfig.name : (col.header ? col.header : columnConfig.name);
                Object.keys(columnConfig).forEach(
                    (key) => (col[key] = columnConfig[key])
                );
                return { ...col, ...columnConfig };
            }
            return col;
        });

        if (isOldColumn) {
            this.view.options.columns = viewColumns;
            this.listConfig.listData = this.listConfig.listData.map(col => {
                if (col.id === columnConfig.id) {
                    return { ...col, ...columnConfig };
                }
                return col;
            });
            this.updateListColumns();
            this.view = { ...this.view };
            this.getCurrentViewStore()?.handleViewChange?.(columnConfig);
            const viewAggregationStoreColumns = this.viewAggregationStore.columns.map(col => {
                if (col.id === columnConfig.id) {
                    col.header = this.view.is_main_view == 1 ? columnConfig.name : (col.header ? col.header : columnConfig.name);
                    Object.keys(columnConfig).forEach(
                        (key) => (col[key] = columnConfig[key])
                    );
                    return { ...col, ...columnConfig };
                }
                return col;
            });
            this.setViewAggregationStoreData(viewAggregationStoreColumns);
            return;
        }

        if (!columnConfig.dataIndex) {
            columnConfig.dataIndex = `column_${columnConfig.id}`;
        }
        this.view.options.columns = [...this.view.options.columns, columnConfig];
        this.listConfig.listData = [...this.listConfig.listData, columnConfig];
        this.updateListColumns();
        this.view = { ...this.view };
        this.getCurrentViewStore()?.handleViewChange?.(columnConfig, isScrollToAddColumn);
        this.setViewAggregationStoreData([...this.viewAggregationStore.columns, columnConfig]);
    }

    get isSaveResetButtonsOpen() {
        return this.getCurrentViewStore()?.modified || this.unSavedViewOfEditReport;
    }

    updateListColumns() {
        TL.list.reports.ColumnsFormatter.addListColumns(this.listConfig.list.id, Ext.clone(this.listConfig.listData)) //todo get rid of this
    }

    toggleSaveResetButtonsPostion() {
        this.saveResetButtonsPosition = this.saveResetButtonsPosition === "snackbar" ? "toolbar" : "snackbar";
    }

    resetSaveResetButtonsPostion() {
        this.saveResetButtonsPosition = "snackbar";
    }

    resetRunningReportData = async (isResetView = false) => {
        this.isViewInEditMode = false;
        if (this.unSavedViewOfEditReport && isResetView) {
            this.handleViewChange(this.initialViewOfEditReport.id, true, this.initialViewOfEditReport);
        }
        this.initialViewId = null;
        this.unSavedViewOfEditReport = null;
        this.initialViewOfEditReport = null;
    }

    async handleDeleteColumn(columnId) {
        try {
            await ApiClient.List.deleteColumn(this.listId, columnId);
        } catch (e) {
            console.error(e);
            this.appStore.notificationStore.showNotification(e?.response?.messages ?? "Something went wrong please try again", "error");
            return false;
        }

        this.fetchListConfig();
        this.fetchView(this.view.id, true);
        return true;
    }

    async loadGrid() {
        await this.fetchListConfig();
        this.fetchViews(false, false);
        this.fetchView(this.view.id, true);
        this.notificationsStore.fetchData();
        this.remindersStore.fetchData();
    }

    loadView = async (viewId) => {
        this.fetchViews(false, false);
        this.handleViewChange(viewId);
    }

    getViewForEditReport = async (viewId) => {
        try {
            const resp = await ApiClient.Views.getView({
                list_id: this.listId,
                view_id: viewId,
            });
            return resp.data
        } catch (e) {
            console.log(e);
        }
    }

    getViewsForEditReport = async () => {
        const listId = this.listId
        try {
            const response = await ApiClient.Views.getViews({
                list_id: listId,
            });
            return response.data.filter(view => view.type !== "dashboard");
        } catch (e) {
            console.error(e);
        }
    }

    fetchUserEvents = async () => {
        if (this.custom_events) {
            return this.custom_events;
        }
        try {
          let resp = await ApiClient.List.getUserEvents({
            list_id: this.listId
          });
          this.custom_events = resp.data;
          return resp.data;
        } catch (e) {
          console.log(e);
        }
    }
    
    async loadGridAfterViewSave() {
        this.fetchViews(false, false);
        this.fetchView(this.view.id, true);
    }

    async clearCache() {
        // Object.keys(this.viewStores).forEach(viewId => {
        //     if (this.view.id === viewId) {
        //         return;
        //     }
        //     delete this.viewStores[viewId];
        // })
    }

    get defaultView() {
        return this.views.find((view) => view.is_default == "1") ?? this.views[0];
    }

    async changeCriteria(criteria) {
        // this.view.criteria = criteria;
        // const view = await this.saveView();
        // if (view) {
        //     this.view.criteria = view.criteria;
        // }
        // this.currentViewStore.isViewCriteriaModified = true;
        this.getCurrentViewStore().setModifiedCriteria?.(criteria);
        // this.view.id = -1;
        await this.getCurrentViewStore().refreshData?.();
        //For client row model header colors were not refreshing
        this.getCurrentViewStore().setAgGridColumnDefs();
    }

    getColorValue(colorVal, colId) {
        return {
            color: colorVal.color,
            bg_color: colorVal.bg_color,
            column_id: colId,
            criteria: colorVal.criteria.query_string,
            is_dropdown_row_coloring: colorVal.is_dropdown_row_coloring,
        }
    }

    async saveColorCriteria(colorValues) {
        let colorCodes = [];
        colorValues.forEach((colorVal) => {
            if (
                colorVal.column_id &&
                typeof colorVal.column_id !== "string"
            ) {
                colorVal.column_id.forEach((colId) => {
                    colorCodes.push(this.getColorValue(colorVal, colId));
                });
            } else {
                colorCodes.push(this.getColorValue(colorVal, colorVal.column_id));
            }
        });
        try {
            const resp = await ApiClient.List.updateColorCodes({
                list_id: this.listId,
                codes: JSON.stringify(colorCodes),
            });
            const response = await ApiClient.List.getColorCodes({
                list_id: this.listId,
            });
            this.listConfig.colorCodes = response.data;
        } catch (e) {
            console.error(e);
            this.appStore.notificationStore.showNotification(e?.response?.messages ?? "Something went wrong please try again", "error");
        }
        await this.getCurrentViewStore().refreshData?.();
        this.getCurrentViewStore().addGrid?.();
    }

    getSubsheetListColumns(value, type = "id") {
        const columns = this.listConfig.listData.find(col => col[type] === value)?.list_columns ?? [];
        columns.forEach(col => {
            if (!col.dataIndex) {
                col.dataIndex = col.dataIndex ?? "column_" + col.id;
            }
        })
        return columns;
    }

    changeSubsheetColumnsPosition(columnId, columnOrder = {}) {
        let subsheetColumns =
          this.listConfig?.listData?.find((col) => col["id"] === columnId)?.list_columns ??
          [];
        subsheetColumns.forEach((column) => {
          for (const key in columnOrder) {
            if (column.hasOwnProperty("order") && column.id == key) {
              column.order = columnOrder[key];
            }
          }
        });
        const indexOfColumnToRemove = subsheetColumns.findIndex(
          (column) => column.alias_name == "createdBy" && column.default_type == "1"
        );
        const createdByColumn = subsheetColumns.splice(indexOfColumnToRemove, 1)[0];
        subsheetColumns = subsheetColumns.slice().sort((a, b) => a.order - b.order);
        subsheetColumns.splice(indexOfColumnToRemove, 0, createdByColumn);
      
        this.listConfig?.listData?.forEach((column, index) => {
          if (column.id == columnId) {
            this.listConfig.listData[index].list_columns = subsheetColumns;
          }
        });
    }

    handleSubsheetAddDropdownOption(columnConfig, subsheetColumnId, listColumns = this.getSubsheetListColumns(subsheetColumnId)) {
        listColumns.forEach((col) => {
            if (col.id === columnConfig.id) {
                col.header = columnConfig.name;
                Object.keys(columnConfig).forEach(
                    (key) => (col[key] = columnConfig[key])
                );
                return { ...col, ...columnConfig };
            }
            return col;
        });
    }

    handleSubsheetColumnConfigChange(columnConfig, subsheetColumnId, listColumns = this.getSubsheetListColumns(subsheetColumnId)) {
        let isOldColumn = false;
        listColumns.forEach((col) => {
            if (col.id === columnConfig.id) {
                isOldColumn = true;
                col.header = columnConfig.name;
                Object.keys(columnConfig).forEach(
                    (key) => (col[key] = columnConfig[key])
                );
                return { ...col, ...columnConfig };
            }
            return col;
        });

        !isOldColumn && listColumns.push(columnConfig);

        return listColumns;
    }

    async handleSubsheetColumnDelete(columnId, subsheetColumnId, listColumns = this.getSubsheetListColumns(subsheetColumnId)) {
        try {
            await ApiClient.List.deleteColumn(undefined, columnId);
        } catch (e) {
            console.error(e);
            Ext.Msg.alert(
                "error",
                e?.response?.messages ?? "Something went wrong please try again"
            );
            return false;
        }
        const index = listColumns.findIndex((col) => col.id === columnId);
        listColumns.splice(index, 1);

        return listColumns;
    }

    saveView = async (view = this.view) => {
        try {
            const filteredView = this.getviewToSaveWithoutHeaderInColumns(view);
            const resp = await ApiClient.Views.saveView({
                list_id: this.listId,
                view: filteredView,
                is_edit: true,
            })
            if (view.id <= 0) {
                this.views.push(resp.data.view);
            }
            return resp.data.view;
        } catch (e) {
            Ext.Msg.alert("Error", e.response?.messages ?? e.response?.message ?? "Something went wrong. Please try again")
            console.log(e);
        }
    };

    deleteView = async (viewId) => {
        try {
            if(parseInt(viewId) <= 0) {
                return;
            }
            const resp = await ApiClient.Views.deleteView({
                view_id: viewId,
                list_id: this.listId,
                is_web: 1
            });
            if (resp.success) {
                this.appStore.notificationStore.showNotification("Report has been deleted successfully", "success");
                //await this.fetchViews(false, false);
                const viewsData = await this.getViewsForEditReport();
                const defaultView = viewsData.find((view) => view.is_default == "1") ?? this.views[0];
                const viewInfo = await this.getViewForEditReport(defaultView.id);
                this.appStore.currentViewStore = null;
                this.views = viewsData;
                this.view = viewInfo;
                this.currentViewStore?.showLoadingOverlay?.();
                this.currentViewStore = null;
                this.setCurrentViewStore(viewInfo);
            }
        } catch (e) {
            this.appStore.notificationStore.showNotification(e?.response?.messages ?? "Something went wrong please try again", "error");
            console.log(e);
        }
    };

    getEditRowConfig = (rowId, copiedRowId) => {
        let index;
        if (rowId !== "") {
            index = this.currentViewStore?.allRowsData.findIndex(row => row.row_id == rowId);
        }
        return {
            rowId: rowId,
            copiedRowId: copiedRowId ?? "",
            isPreviousExist: index > 0 ? true : false,
            isNextExist: index >= 0 && index < this.currentViewStore.allRowsData.length - 1 ? true : false
        };
    }

    getviewToSaveWithoutHeaderInColumns(view) {
        if(view.is_main_view != 1) {
            return view;
        }
        const finalView = cloneDeep(view);
        let finalColumns = finalView.options.columns;
        for (let i = 0; i < finalColumns.length; i++) {
          if(finalColumns[i]["header"]) {
            delete finalColumns[i]["header"];
          }
        }
        return finalView;
    }

    handleBulkPrint = (rowIds) => {
        const formPermissions = Ext.create("TL.list.permissions.Permissions", {
            permissions: this.permissions?.permissions
        });

        this.printWindow = Ext.create('TL.list.PrintWindow', {
            rowIds: rowIds,
            viewId: this.view.id,
            listId: this.listConfig.list.id,
            templateId: 1,
            listName: this.listConfig.list.name,
            columns: this.listConfig.listData,
            permissions: formPermissions
        });

        this.printWindow.show();
    }

    handlePrint = (rowId) => {
        const formPermissions = Ext.create("TL.list.permissions.Permissions", {
            permissions: this.permissions?.permissions
        });

        this.printWindow = Ext.create('TL.list.PrintWindow', {
            rowId: rowId,
            viewId: this.view.id,
            listId: this.listConfig.list.id,
            templateId: 1,
            listName: this.listConfig.list.name,
            columns: this.listConfig.listData,
            permissions: formPermissions
        });

        this.printWindow.show();
    }

    isSubsheetDataModified = (subsheetData) => {
        try {
            return subsheetData['deletedRows'].length > 0 || subsheetData['newRows'].length > 0 || Object.keys(subsheetData['updatedRows']).length > 0;
        } catch (error) {
            return false;
        }
    }

    showForm = (title, rowId, copiedRowId, rowData) => {
        if (
          this.view?.type == "table" &&
          this.view?.list_id &&
          this.view?.options?.main_list_id &&
          this.view?.list_id != this.view?.options?.main_list_id
        ) {
          for (let i = 0; i < this.currentViewStore?.allRowsData?.length; i++) {
            if (
              title == "Edit" &&
              this.currentViewStore?.allRowsData[i]?.row_id == rowId
            ) {
              rowId = this.currentViewStore?.allRowsData[i]?.parent_row_id;
              break;
            } else if (
              title == "New" &&
              copiedRowId &&
              this.currentViewStore?.allRowsData[i]?.row_id == copiedRowId
            ) {
              copiedRowId = this.currentViewStore?.allRowsData[i]?.parent_row_id;
              break;
            }
          }
        } 
        copiedRowId = copiedRowId ?? "";
        const formPermissions = Ext.create("TL.list.permissions.Permissions", {
            permissions: this.permissions?.permissions
        });
        if (this.formPanel) {
            this.formPanel.destroy();
        }

        this.formPanel = Ext.create("TL.list.form.FormPanelInCard", {
            //anchor: "100%",
            itemId: "add-form",
            cls: "tl-view-container form-panel-in-card",
            border: false,
            //container: 'new-entry-ct-' + me.getId (),
            viewId: this.view.id,
            hidingEnabled: true,
            rowData: (typeof rowData == "undefined") ? this.getEditRowConfig(rowId, copiedRowId) : rowData,
            permissions: formPermissions,
            listData: {
                listId: this.listId,
                event_handlers: this.listConfig.list.event_handlers,
                columns: this.listConfig.listData, // Pass by reference and this is mutable inside the component
                name: this.listConfig.list.name
            },
            listeners: {
                "success": async function (scope, data, rowId, addAnother, resp) {
                    if (this.view?.type == "table") {
                        try {
                            const oldRowNode = {...this.currentViewStore.getRowNodeById(rowId)?.node};
                            await this.currentViewStore.refreshData?.();
                            let rowNode = this.currentViewStore.gridApi.getRowNode(rowId);
                            const rowData = rowNode ? rowNode.data : resp.row;
                            if (oldRowNode && oldRowNode.data && Object.keys(oldRowNode.data).length > 0) {
                                let updatedCellData = {};
                                Object.entries(data).forEach(([columnId, value]) => {
                                    if (value['value'] !== oldRowNode.data[columnId]) {
                                        this?.currentViewStore?.columns.forEach(column => {
                                            if (column.dataIndex == 'column_' + columnId) {
                                                if (!updatedCellData[rowId]) {
                                                    updatedCellData[rowId] = [rowData, {'columnName': column?.name, 'columnPath': column?.alias_path}];
                                                } else {
                                                    updatedCellData[rowId].push({'columnName': column?.name, 'columnPath': column?.alias_path});
                                                }
                                            }
                                        });
                                    }
                                });
                                if (Object.keys(updatedCellData).length > 0) {
                                    handleUpdatingData(JSON.stringify(updatedCellData), '', this.currentViewStore.listId, this.currentViewStore.isSubsheet);
                                }
                            } else {
                                handleUpdatingData('', JSON.stringify([rowData]), this.currentViewStore.listId, this.currentViewStore.isSubsheet);
                            }
                            
                            if (resp && resp?.updatedSubsheetData) {
                                const modifiedSubsheetData = {};
                                let isModified = false;
                                Object.entries(resp.updatedSubsheetData).forEach(([listId, info]) => {
                                    if (this.isSubsheetDataModified(info)) {
                                        modifiedSubsheetData[listId] = info;
                                        isModified = true;
                                    }
                                });
                                if (isModified) {
                                    handleSubsheetDataUpdationThroughForm(JSON.stringify(modifiedSubsheetData), this.currentViewStore.listId, rowId);
                                }
                            }

                            if (rowNode) {
                                this.currentViewStore.refreshRowForColoring(rowNode);
                            }
                            if (Object.keys(this.currentViewStore?.duplicateColumnsData).length > 1) {
                                const highlightedColumnIds = Object.keys(this.currentViewStore.duplicateColumnsData);
                                highlightedColumnIds.forEach(columnId => {
                                    if (columnId !== "themeColor") {
                                        const colId = columnId.split('_').pop();
                                        var newValue = data[colId]?.value ?? '';
                                        var oldValue = Object.keys(oldRowNode).length > 0 ? oldRowNode?.data[columnId] : '';
                                        if (this.currentViewStore?.isValidCellValue(data[colId]['column_type_id'], newValue) && oldValue != newValue) {
                                            this.currentViewStore?.modifyDuplicatesDataCount(columnId, newValue, true, rowId, '', !this.currentViewStore?.isLazyLoadEnabled);
                                        }
                                        if (this.currentViewStore?.isValidCellValue(data[colId]['column_type_id'], oldValue) && oldValue != newValue) {
                                            this.currentViewStore?.modifyDuplicatesDataCount(columnId, oldValue, false, rowId, '', !this.currentViewStore?.isLazyLoadEnabled);
                                        }
                                    }
                                });
                            }
                        } catch (e) {
                            console.log("Error", e);
                        }
                    } else {
                        window.appStore?.currentViewStore?.executeActionCommand('reload', []);
                    }
                },
                "close": function () {
                    this.formPanel.destroy();
                    formWin.close();
                },
                'fetchnext': function (rowId) {
                    let index = this.currentViewStore?.allRowsData.findIndex(row => row.row_id == rowId);
                    const nextEditRowIndex = this.currentViewStore?.allRowsData[index + 1]["row_id"];
                    this.formPanel.reloadRow(this.getEditRowConfig(nextEditRowIndex));
                },
                'fetchprevious': function (rowId) {
                    let index = this.currentViewStore?.allRowsData.findIndex(row => row.row_id == rowId);
                    const prevEditRowIndex = this.currentViewStore?.allRowsData[index - 1]["row_id"];
                    this.formPanel.reloadRow(this.getEditRowConfig(prevEditRowIndex));
                },
                'loadGrid': async function () {
                    await this.loadGrid();
                },
                "scope": this
            }
        })

        let formWin = Ext.create("TL.Window", {
            title: title,
            modal: true,
            width: Ext.getBody().getViewSize().width - 100,
            height: Ext.getBody().getViewSize().height - 20,
            layout: 'fit',
            autoScroll: true,
            items: [this.formPanel]
        });

        formWin.show();
    }

    executeReportActions = async (event, records) => {
        try {
            let rowIds = [];
            if (typeof records == 'undefined' || !records.length) {
                this.appStore.notificationStore.showNotification("No Records!", "error");
                return;
            }
            for (var i = 0, l = records.length; i < l; i++) {
                let rowId = records[i]?.data?.row_id;
                if (rowId) {
                    rowIds.push(rowId);
                }
            }
            console.log("rowIds in executeReportActions", rowIds);
            if(event && event['id']) {
                console.log("event id is", event['id']);
                switch (event['id']) {
                    case "edit":
                        this.showForm("Edit", rowIds[0]);
                        return;
                    case "duplicate":
                        this.showForm("New", "", rowIds[0]);
                        return;
                    case "delete":
                        try {
                            await this.appStore.notificationStore.confirm({
                            description: "This will delete the selected row",
                            });
                            await this.deleteRow(rowIds[0]);
                        } catch (e) {}
                        return;
                    case "add_event":
                        this.showAddEventPanel();
                        return;
                    default:
                        console.log(`Unknown action: ${event.id}`);
                        return;
                            
                }
            }
        } catch (e) {
            console.error(e);
            this.appStore?.notificationStore?.showNotification(
                e?.response?.messages ?? "Something went wrong while executing action",
                "error"
            );
        }
    }

    deleteRow = async (rowId) => {
        try {
            let rowIdsList = [];
            if (rowId) {
              rowIdsList.push(rowId);
            }
            let rowIds = rowIdsList.join();
            if (rowIds.length > 0) {
              try {
                const resp = await ApiClient.List.deleteAction({
                  list_id: this.listId,
                  delete_row_id: rowIds,
                  parent_column_id : "",
                  parent_row_id : "",
                  parent_list_id : "",
                });
                if (resp.success) {
                    window.appStore?.currentViewStore?.executeActionCommand('reload', []);
                }
              } catch (e) {
                Ext.Msg.alert("Error", e.message ?? "Something went wrong");
              }
            }
        } catch (e) {
            Ext.Msg.alert("Error", e.message ?? "Something went wrong");
        }
    }

    showAddEventPanel = () => {
        try {
            this.addEventWindow = Ext.create("TL.Window", {
                title: "Add Action Button",
                modal: true,
                width: 1000,
                height: Ext.getBody().getViewSize().height,
                layout: 'fit',
                items: [Ext.create("TL.list.events.AddEventPanel", {
                    autoScroll: true,
                    anchor: "100%",
                    border: false,
                    currentList: {
                        id: this.listConfig.list.id,
                        name: this.listConfig.list.name,
                        columns: this.listConfig.listData,
                    },
                    showHandlersOnSuccess: true,
                    listeners: {
                        close: function () {
                            try { this.addEventWindow.close(); } catch (e) { console.error(e); }
                        },
                        success: async function () {
                            try {
                                console.log("success");
                                // await this.loadGrid();
                                // this.fireEvent('reloadsheet', this);
                            } catch (e) {
                                console.error(e);
                            }
                        },
                        scope: this
                    }
                })]
            });

            this.addEventWindow.show();
        } catch (e) {
            console.error(e);
            this.appStore?.notificationStore?.showNotification(
                e?.response?.messages ?? "Something went wrong while opening Add Event panel",
                "error"
            );
        }
    }

    getListConfigForViewsService = async () => {
        const userEvents = await this.fetchUserEvents();

        const listConfigForViewsService = {
            color_codes: this.listConfig.colorCodes,
            custom_events: userEvents,
            list: this.listConfig.list,
            list_columns: this.listConfig.listData,
            list_permissions: this.listConfig.listPermissions
        }
        return listConfigForViewsService;
    }

    handleEditReport = async (viewId, isEditMode = false, existingView) => {
        let viewData;
        if (!isEditMode) {
            viewData = await this.getViewForEditReport(viewId);
        } else {
            viewData = existingView;
        }

        const listConfigForViewsService = await this.getListConfigForViewsService();

        const viewPermissions = Ext.create("TL.list.permissions.Permissions", {
            permissions: this.listConfig.listPermissions
        });

        const viewsService = Ext.create(TL.list.views.Service, {
          //containerId: me.id,
          config: listConfigForViewsService,
          listeners: {
              'executereport': Ext.bind(async function (viewInfo) {
                  this.isViewInEditMode = true;
                  if(!isEditMode) {
                    this.initialViewId = viewId;
                  }
                  if (viewInfo.type == "table") {
                    if (!isEditMode) {
                        this.initialViewOfEditReport = viewData;
                    }
                    if (viewInfo.id > 0) {
                        viewInfo.id = 0;
                    }
                    this.unSavedViewOfEditReport = viewInfo;
                  } else {
                    if (viewInfo.id != -1) {
                        viewInfo.id = 0;
                    }
                  }
                  await this.handleViewChange(viewInfo.id, true, viewInfo);
              }, this),
              "success": Ext.bind(function (viewInfo) {
                  this.resetRunningReportData(false);
              }, this),
              "view-deleted": Ext.bind(function () {
                this.fetchViews(false, true);
              }, this),
              'view-saved': Ext.bind(function (listId, viewId) {
                  this.loadView(viewId);
              }, this),
          }
        });

        viewsService.showAddView(viewData, listConfigForViewsService, viewPermissions, Ext.bind(function (viewInfo) {
          this.loadView(viewInfo.id);
        }, this));
    }

    handleCreateReport = async (type, withOptions) => {
        const listConfigForViewsService = await this.getListConfigForViewsService();

        const viewPermissions = Ext.create("TL.list.permissions.Permissions", {
            permissions: this.listConfig.listPermissions
        });

        const viewsService = Ext.create(TL.list.views.Service, {
          //containerId: me.id,
          config: listConfigForViewsService,
          listeners: {
              'executereport': Ext.bind(async function (viewInfo) {
                  if (viewInfo.type == "table") {
                    if (viewInfo.id > 0) {
                        viewInfo.id = 0;
                    }
                    this.initialViewOfEditReport = cloneDeep(this.view);
                    this.unSavedViewOfEditReport = viewInfo;
                  } else {
                    if (viewInfo.id != -1) {
                        viewInfo.id = 0;
                    }
                  }
                  await this.handleViewChange(viewInfo.id, true, viewInfo);                  
              }, this),
              "success": Ext.bind(function (viewInfo) {
                  this.resetRunningReportData(false);
              }, this),
              "view-deleted": Ext.bind(function () {
                this.fetchViews(false, true);
              }, this),
              'view-saved': Ext.bind(function (listId, viewId) {
                  this.loadView(viewId);
              }, this),
          }
        });
        viewsService.showAddView(viewsService.getViewTemplate(type, listConfigForViewsService.list, withOptions), listConfigForViewsService, viewPermissions, Ext.bind(function (viewInfo) {
          this.loadView(viewInfo.id);
        }, this));
    }

    async fetchInitialData(isForce = false, isSilent = false) {
        if (!isForce && this.isInitialDataLoaded) {
            this.isLoading = false;
            return;
        }

        !isSilent && (this.isLoading = true)
        await Promise.all([this.fetchListConfig(this.listId), this.fetchViews(isSilent)]);
        if (!this.currentViewStore) {
            if (this.defaultView?.id) {
                this.fetchView(this.defaultView.id, isSilent);
            }
        }
        this.isInitialDataLoaded = true;
        !isSilent && (this.isLoading = false)
    }

    async fetchView(viewId, isSilent = false) {
        // if (this.view) {
        //     return;
        // }
        !isSilent && (this.isLoading = true);
        const startTime = new Date();
        const resp = await ApiClient.Views.getView({
            list_id: this.listId,
            view_id: viewId,
        });
        const endTime = new Date();
        this.isSlowInternet = Math.abs(endTime - startTime) > 4000; // consider slow if it takes more than 4 seconds
        if (this.isSlowInternet) {
            console.log("Slow Internet Detected....");
        }
        !isSilent && (this.isLoading = false);

        this.currentViewStore = null;
        this.setCurrentViewStore(resp.data);
        this.view = resp.data;
        this.setViewAggregationStoreData(resp.data.options?.columns);
        this.getColumnConfigs(viewId)
    }

    addPathToWindow(column) {
        let str = ""
        column?.path?.forEach(el => {
            if (el.alias_name) {
                str = str + (str.length > 0 ? "." : "") + el.alias_name
            }
        })
        window.hasOwnProperty("percentageAliases") ? window.percentageAliases.push(str) : window["percentageAliases"] = [str]
    }

    addColInfoToWindows(column, colInfo) {
        let str = ""
        column?.path?.forEach(el => {
            if (el.alias_name) {
                str = str + (str.length > 0 ? "." : "") + el.alias_name
            }
        })
        window.hasOwnProperty("lookupColumnsInfo") ? window.lookupColumnsInfo[str] = colInfo : window["lookupColumnsInfo"] = { [str]: colInfo }
    }

    getColumnConfigs(viewId) {
        let currentView = this.views.filter(view => view.id == viewId)
        currentView = currentView.length ? currentView[0] : null
        let criteria = ""
        if(currentView && currentView.criteria && currentView.criteria.length > 0){
            criteria = currentView.criteria
        }
        if (criteria.length > 0) {
            criteria = JSON.parse(criteria)
            criteria && criteria.columns?.forEach(column => {
                let path = column.path
                let last = path && path[path.length - 1]
                if (path.length <= 2) {
                    return;
                }
                let list_id = last.list_id, column_id = last.id
                ApiClient.List.getColumnConfigs({
                    list_id: list_id,
                    column_id: column_id
                }).then(response => {
                    if (response.success) {
                        this.addColInfoToWindows(column, response.data)
                        if (response.data.column_type_id == 22) {
                            this.addPathToWindow(column)
                        } else if (response.data.column_type_id == 10) {
                            let options = JSON.parse(response.data.options)
                            if ( options && options[0].column_type == "percentage") {
                                this.addPathToWindow(column)
                            }
                        }
                    }
                })
            })
        }
    }

    async fetchViews(isSilent = false, isFetchView = true) {
        const listId = this.listId
        try {
            const response = await ApiClient.Views.getViews({
                list_id: listId,
            });

            this.views = response.data.filter(view => view.type !== "dashboard");
            if (isFetchView && this.listConfig) {
                await this.fetchView(this.defaultView.id, isSilent);
            }
        } catch (e) {
            console.error(e);
        }
    }

    async fetchListConfig() {
        try {
            let resp = await ApiClient.List.getListConfig({
                list_id: this.listId,
            });

            this.listConfig = resp.data;
            this.permissions = new Permissions(resp.data.listPermissions);
            if (this.permissions?.isAdmin()) {
                this.remindersStore = new RemindersStore(this.appStore, this);
                this.notificationsStore = new RemindersStore(this.appStore, this, false);

                // if (this.listId === userListId) {
                //     TableStore.addSpecialColumns(this.listConfig.listData)
                // }
            }
            this.updateListColumns();

            // this.containers[this.listId].listConfig = this.listConfig;
        } catch (e) {
            console.log(e);
        }
    }

}

decorate(ContainerStore, {
    views: observable,
    view: observable,
    isLoading: observable,
    listConfig: observable,
    saveResetButtonsPosition: observable,
    unSavedViewOfEditReport: observable,
    commentsPopoverData: observable,
    remindersStore: observable,
    isSlowInternet: observable,
    currentViewStore: observable,

    isSaveResetButtonsOpen: computed,
});


export default ContainerStore;
export const ContainerStoreContext = createContext()
export const ViewStoreContext = createContext();